import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Document, View } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { ChecklistTab } from '../data/checklistData';
import {
  ChatbeatPdfTemplate,
  ChatbeatPdfService,
  CoverTitle,
  SectionHeader,
  ChecklistItem,
  Paragraph,
  BulletList,
} from '../../../shared/services/pdf';

interface ChecklistPdfExportProps {
  projectName: string;
  activeTab: ChecklistTab;
  checkedTasks: Record<string, string>;
  /** Klasa przycisku — na telefonie ikona w górnym pasku (`mobile-header__icon-btn`). */
  buttonClassName?: string;
}

interface ChecklistDocumentProps {
  activeTab: ChecklistTab;
  checkedTasks: Record<string, string>;
  t: (key: string, options?: { defaultValue?: string }) => string;
}

const ChecklistDocument: React.FC<ChecklistDocumentProps> = ({ activeTab, checkedTasks, t }) => {
  const hasBullets = activeTab.introBulletKeys.length > 0;

  return (
    <Document>
      <ChatbeatPdfTemplate>
        <CoverTitle title={t(activeTab.introTitleKey)} subtitle={t(activeTab.introSubtitleKey)} />
        <Paragraph>{t(activeTab.introDescKey)}</Paragraph>

        {hasBullets && (
          <>
            <Paragraph>{t(activeTab.introHelperKey)}</Paragraph>
            <BulletList items={activeTab.introBulletKeys.map((k) => t(k))} />
          </>
        )}

        <Paragraph>{t(activeTab.introFooterKey)}</Paragraph>
        {activeTab.introFooter2Key && <Paragraph>{t(activeTab.introFooter2Key)}</Paragraph>}

        {activeTab.sections.map((section) => (
        <View key={section.id}>
          <SectionHeader
            title={t(section.titleKey, { defaultValue: section.defaultTitle })}
            subtitle={
              section.subtitleKey
                ? t(section.subtitleKey, { defaultValue: section.defaultSubtitle })
                : undefined
            }
          />
          {section.tasks.map((task, idx, arr) => {
            const tailSize = Math.min(3, arr.length);
            const tailStart = arr.length - tailSize;

            if (idx > tailStart && idx < arr.length) return null;

            if (idx === tailStart && tailSize > 1) {
              return (
                <View key={task.id} wrap={false}>
                  {arr.slice(tailStart).map((t2) => (
                    <ChecklistItem
                      key={t2.id}
                      title={t(t2.titleKey, { defaultValue: t2.defaultTitle })}
                      description={
                        t2.descriptionKey
                          ? t(t2.descriptionKey, { defaultValue: t2.defaultDescription })
                          : undefined
                      }
                      checked={t2.id in checkedTasks}
                    />
                  ))}
                </View>
              );
            }

            return (
              <ChecklistItem
                key={task.id}
                title={t(task.titleKey, { defaultValue: task.defaultTitle })}
                description={
                  task.descriptionKey
                    ? t(task.descriptionKey, { defaultValue: task.defaultDescription })
                    : undefined
                }
                checked={task.id in checkedTasks}
              />
            );
          })}
        </View>
      ))}
      </ChatbeatPdfTemplate>
    </Document>
  );
};

const ChecklistPdfExport: React.FC<ChecklistPdfExportProps> = ({
  projectName,
  activeTab,
  checkedTasks,
  buttonClassName = 'action-btn',
}) => {
  const { t } = useTranslation();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (isExporting) return;

    try {
      setIsExporting(true);
      const filename = `Checklist-${projectName}-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
      const document = <ChecklistDocument activeTab={activeTab} checkedTasks={checkedTasks} t={t} />;
      await ChatbeatPdfService.generate(document, filename);
    } catch (error) {
      console.error('PDF export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button className={buttonClassName} onClick={handleExport} disabled={isExporting}>
      {isExporting ? (
        <span className="ico--loader ico--xs" />
      ) : (
        <span className="ico--download ico--xs" />
      )}
    </Button>
  );
};

export { ChecklistPdfExport };
export default ChecklistPdfExport;
